import { useMemo, useState } from 'react';
import { DynamicFieldKey_Enum, Template } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import {
  BatchCreateReviewInvitationResponse,
  InvitationRecipient,
} from '@weave/schema-gen-ts/dist/schemas/reviews/invitation/v1/service.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { PetsV1 } from '@frontend/api-pets';
import { ReviewInvitationMutations } from '@frontend/api-reviews-invitation';
import { useTranslation } from '@frontend/i18n';
import { getStaticTemplateId, ManualTemplateTypes } from '@frontend/integrated-messaging';
import { formatDynamicFieldKey } from '@frontend/string';
import { petIsActive } from '@frontend/vet-components';
import { useAlert } from '@frontend/design-system';

export const PET_NAME_KEY = formatDynamicFieldKey(DynamicFieldKey_Enum.PET_NAME);
export const PET_NAMES_KEY = formatDynamicFieldKey(DynamicFieldKey_Enum.PET_NAMES);
export const PET_TAG_PATTERN =
  /\[?\{\{\s*pet[ _]?names?\s*\}\}\]?|\[?\{\s*pet[ _]?names?\s*\}\]?|\[\s*pet[ _]?names?\s*\]/i;
export const PET_TAG_REPLACE_PATTERN =
  /\[?\{\{\s*pet[ _]?names?\s*\}\}\]?|\[?\{\s*pet[ _]?names?\s*\}\]?|\[\s*pet[ _]?names?\s*\]/gi;

export const replacePetTags = (template: string, petName: string) =>
  template.replace(PET_TAG_REPLACE_PATTERN, () => petName);

export type BulkReviewStep = 'recipients' | 'send' | 'template-selection';

export type UseBulkReviewSendParams = {
  recipients: InvitationRecipient[];
  recipientsWithoutLocation: Person[];
  selectedLocationIds: string[];
  selectedOrgId: string;
  accessibleLocationData: Record<string, { vertical?: Vertical; timezone?: string }>;
  selectedTemplate?: Template;
  singleTemplateType: ManualTemplateTypes;
  templateString: string;
  isVet: boolean;
  getPetNameEnabled: (locationId?: string) => boolean;
  currentStep: BulkReviewStep;
  onSuccess: () => void;
  onDeselectPersons?: (persons: Person[]) => void;
};

export const useBulkReviewSend = ({
  recipients,
  recipientsWithoutLocation,
  selectedLocationIds,
  selectedOrgId,
  accessibleLocationData,
  selectedTemplate,
  singleTemplateType,
  templateString,
  isVet,
  getPetNameEnabled,
  currentStep,
  onSuccess,
  onDeselectPersons,
}: UseBulkReviewSendParams) => {
  const alert = useAlert();
  const { t } = useTranslation('bulk-review-invitations');
  const [isSending, setIsSending] = useState(false);

  const { mutateAsync: sendReviewInvitation } = ReviewInvitationMutations.useBatchCreateReviewInvitationMutation();

  const hasPetTag = useMemo(() => PET_TAG_PATTERN.test(templateString), [templateString]);

  const petQueries = PetsV1.Queries.useFetchPetsByPersonIdQueries(
    recipients.map((recipient) => ({
      req: {
        personId: recipient.person.personId,
        locationId: recipient.locationId,
      },
      options: {
        enabled:
          isVet &&
          getPetNameEnabled(recipient.locationId) &&
          hasPetTag &&
          currentStep !== 'recipients' &&
          recipients.length > 0,
      },
    }))
  );

  const isPetsLoading = useMemo(() => {
    if (!isVet || !hasPetTag || recipients.length === 0 || currentStep === 'recipients') {
      return false;
    }
    return recipients.some((recipient, index) => {
      if (!getPetNameEnabled(recipient.locationId)) {
        return false;
      }
      return petQueries[index]?.isLoading;
    });
  }, [isVet, hasPetTag, recipients, currentStep, getPetNameEnabled, petQueries]);

  const isPetsError = useMemo(() => {
    if (!isVet || !hasPetTag || recipients.length === 0 || currentStep === 'recipients') {
      return false;
    }
    return recipients.some((recipient, index) => {
      if (!getPetNameEnabled(recipient.locationId)) {
        return false;
      }
      return petQueries[index]?.isError;
    });
  }, [isVet, hasPetTag, recipients, currentStep, getPetNameEnabled, petQueries]);

  const petNameByPersonId = useMemo(() => {
    const map = new Map<string, string>();
    recipients.forEach((recipient, index) => {
      const query = petQueries[index];
      if (query?.data?.pets) {
        const activePet = query.data.pets.find((pet) => petIsActive(pet.activeStatus));
        map.set(recipient.person.personId, activePet?.name ?? '');
      }
    });
    return map;
  }, [recipients, petQueries]);

  const handleSendOrSchedule = ({ sendAt, pausable }: { sendAt?: string; pausable?: boolean } = {}) => {
    if (!recipients.length) return;

    if (recipientsWithoutLocation.length) {
      alert.error(
        t('Error finding location associated with {{count}} contacts', { count: recipientsWithoutLocation.length })
      );
      return;
    }

    if (isPetsLoading) {
      return;
    }

    if (isPetsError) {
      alert.error(t('Error fetching pet information. Please try again.'));
      return;
    }

    setIsSending(true);
    void (async () => {
      try {
        const templateId =
          !selectedTemplate || selectedTemplate?.templateId === getStaticTemplateId(singleTemplateType)
            ? '' // Don't pass template id if using default static template, use template string instead
            : selectedTemplate?.templateId ?? '';

        const hasAnyPetNameEnabled = isVet && recipients.some((r) => getPetNameEnabled(r.locationId));

        if (hasAnyPetNameEnabled && hasPetTag && recipients.length > 1) {
          const results = await Promise.allSettled(
            recipients.map((recipient) => {
              const rawPetName = petNameByPersonId.get(recipient.person.personId) ?? '';
              const sanitizedPetName = rawPetName.replace(/[{}[\]]/g, '').trim();
              const personalizedTemplateString = replacePetTags(templateString, sanitizedPetName);

              return sendReviewInvitation({
                orgId: selectedOrgId,
                recipients: [recipient],
                // Do not pass templateId when sending personalized template strings;
                // passing templateId causes the backend to re-fetch the unrendered template by ID and ignore templateString.
                templateId: '',
                timezone:
                  accessibleLocationData[recipient.locationId]?.timezone ??
                  accessibleLocationData[selectedLocationIds[0]]?.timezone ??
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                locale: 'en_US',
                templateString: personalizedTemplateString,
                sendAt,
                pausable,
              });
            })
          );

          const fulfilled = results.filter(
            (r): r is PromiseFulfilledResult<BatchCreateReviewInvitationResponse> => r.status === 'fulfilled'
          );
          const rejected = results.filter((r) => r.status === 'rejected');

          const totalSent = fulfilled.reduce((acc, curr) => acc + (curr.value?.invitations?.length ?? 1), 0);

          if (totalSent > 0) {
            alert.success(
              !!sendAt
                ? t('{{count}} Messages Scheduled', { count: totalSent })
                : t('{{count}} Messages Sent', { count: totalSent })
            );
          }

          if (rejected.length > 0) {
            alert.error(
              !!sendAt
                ? t('Error scheduling messages. Please try again.')
                : t('Error sending messages. Please try again.')
            );

            // On partial failure, deselect the succeeded contacts so the user can retry the rest
            const succeededPersons: Person[] = [];
            results.forEach((result, idx) => {
              if (result.status === 'fulfilled') {
                succeededPersons.push(recipients[idx].person);
              }
            });

            if (succeededPersons.length > 0) {
              onDeselectPersons?.(succeededPersons);
            }
          } else {
            // All succeeded
            onSuccess();
          }
        } else {
          const res = await sendReviewInvitation({
            orgId: selectedOrgId,
            recipients,
            templateId,
            timezone:
              accessibleLocationData[selectedLocationIds[0]]?.timezone ??
              Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale: 'en_US',
            templateString,
            sendAt,
            pausable,
          });

          alert.success(
            !!sendAt
              ? t('{{count}} Messages Scheduled', { count: res.invitations.length })
              : t('{{count}} Messages Sent', { count: res.invitations.length })
          );
          onSuccess();
        }
      } catch {
        alert.error(
          !!sendAt ? t('Error scheduling messages. Please try again.') : t('Error sending messages. Please try again.')
        );
      } finally {
        setIsSending(false);
      }
    })();
  };

  return {
    isSending,
    isPetsLoading,
    isPetsError,
    hasPetTag,
    handleSendOrSchedule,
    petQueries,
    petNameByPersonId,
  };
};
